import { lazy, type ComponentType, type LazyExoticComponent } from 'react'
import type { EmbeddedComponentProps } from '../types'

/**
 * The embedded-SDK providers of this build, keyed by provider id (the pluggability rule: adding one is a folder
 * under `providers/` plus one line here). Each component is its own lazy chunk; the member path never imports a
 * provider SDK statically (AC31, checked by `check:chunks`).
 */
export interface EmbeddedEntry {
  Component: LazyExoticComponent<ComponentType<EmbeddedComponentProps>>
  /** Starts loading the chunk (called once, when the member reaches the face step of this provider). */
  preload: () => Promise<unknown>
  /** True when the provider's check can flash and the member must be asked the photosensitivity question first. */
  asksPhotosensitivity: (publicSettings: Record<string, string | number | boolean>) => boolean
}

const once = <T>(load: () => Promise<T>): (() => Promise<T>) => {
  let p: Promise<T> | null = null
  return () => (p ??= load())
}

function entry(
  load: () => Promise<{ default: ComponentType<EmbeddedComponentProps> }>,
  asksPhotosensitivity: EmbeddedEntry['asksPhotosensitivity']
): EmbeddedEntry {
  const loadOnce = once(load)
  return { Component: lazy(loadOnce), preload: loadOnce, asksPhotosensitivity }
}

export const EMBEDDED: Record<string, EmbeddedEntry> = {
  aws: entry(
    () => import('./aws'),
    (settings) => settings.challenge === 'FaceMovementAndLightChallenge'
  ),
  facetec: entry(
    () => import('./facetec'),
    () => false
  ), // T23: no SDK here; the check runs on its own page
}
